import { flash } from "@xola/ui-kit";
import { omit } from "lodash";
import { Experience, FieldPayload, FieldWithPickupOptions, Form, Seller } from "types";
import { api } from "../../../api/api";
import { PreferenceLabels } from "../../../models/DuplicateExperience";
import { isPickupLocationField } from "../../../models/Form/Form";

interface PayloadWithIdProps {
    [key: string]: unknown;
    id: string;
}

interface saveDataProps {
    payload: unknown;
    path: string;
    item: string;
}

export const saveData = async <T>({ payload, path, item }: saveDataProps) => {
    try {
        if (item === PreferenceLabels.FEES) {
            return await api.put<T>(path, payload);
        }

        if (item === PreferenceLabels.AUTO_ACCEPT) {
            return await api.patch<T>(path, payload);
        }

        if (item === PreferenceLabels.EXPERIENCE && isPayloadWithId(payload)) {
            return await api.put<T>(path, payload);
        }

        return await api.post<T>(path, payload);
    } catch {
        flash.danger(`Error saving ${item}.`);
        return null;
    }
};

export const refetchDuplicatedExperience = async (experienceId: string, sellerId: string) => {
    try {
        const { data } = await api.get<Experience>(`/experiences/${experienceId}?seller=${sellerId}`);
        return data;
    } catch {
        flash.danger("Error getting experience");
        return null;
    }
};

export const getPaths = (experience: Experience, seller: Seller) => ({
    forms: "/forms",
    catalogItems: "/catalogItems",
    experiences: `/experiences/${experience.id}`,
    terms: `/experiences/${experience.id}/terms`,
    travelerPreference: `/experiences/${experience.id}/travelerPreference`,
    guide: `/experiences/${experience.id}/guide`,
    review: `/experiences/${experience.id}/preferences/review`,
    waitlist: `/experiences/${experience.id}/waitlist`,
    checkIn: `/experiences/${experience.id}/checkIn`,
    qrCode: `/experiences/${experience.id}/qrCode`,
    schedules: `/experiences/${experience.id}/schedules`,
    waivers: `/experiences/${experience.id}/waiver`,
    autoAccept: `/sellers/${seller.id}`,
    virtualMeeting: `/experiences/${experience.id}/virtualMeeting`,
});

const isPayloadWithId = (payload: unknown): payload is PayloadWithIdProps => {
    if ((payload as PayloadWithIdProps).id) {
        return true;
    }

    return false;
};

export const omitId = <T>(data: any): T => omit(data, ["id"]);

export const getDuplicateFormPayload = (
    form: Form,
    newProductId: string,
): Omit<Form, "id" | "fields"> & { fields: FieldPayload[] } => {
    return {
        ...omit(form, ["id"]),
        product: { id: newProductId },
        fields: form.fields.map((innerField) => {
            const fieldWithoutId = omit(innerField, ["id"]);

            const field: FieldPayload = {
                ...fieldWithoutId,
                ...(fieldWithoutId.catalogItem?.id && {
                    catalogItem: { id: fieldWithoutId.catalogItem.id },
                }),
            };

            if (isPickupLocationField(fieldWithoutId)) {
                const pickupField = fieldWithoutId as FieldWithPickupOptions;
                return {
                    ...field,
                    options: pickupField.options.map((option) => ({
                        ...option,
                        ...(option.pickupLocation?.id && {
                            pickupLocation: { id: option.pickupLocation.id },
                        }),
                    })),
                } as unknown as FieldPayload;
            }
            return field;
        }),
    };
};
